import React, {useEffect, useState} from 'react';
import {useSignalEffect} from '@preact/signals-react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import {WrenchIcon} from 'lucide-react';
import {useThemeContext} from '../../theme/mui-theme';
import {toast} from 'sonner';
import {useTranslation} from 'react-i18next';
import InstallationAssistant from '../assistant/InstallationAssistant';
import ServicePointInfoDialog from '../info/ServicePointInfoDialog';
import {store} from '@/service/store';
import {useNavigate} from 'react-router-dom';
import {useSignals} from "@preact/signals-react/runtime";

interface HeaderProps {
  toggleDrawer: () => void;
  sidebarOpen: boolean;
}

// Helper to get initials from full name
function getInitials(name?: string | null): string {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() ?? '';
  }
  return (
    (parts[0][0] ?? '').toUpperCase() +
    (parts[parts.length - 1][0] ?? '').toUpperCase()
  );
}

const Header = ({ toggleDrawer, sidebarOpen }: HeaderProps) => {
  useSignals();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();

    useEffect(() => {
      const storageKey = 'infoModalShown';
      try {
        if (typeof localStorage !== 'undefined' && !localStorage.getItem(storageKey)) {
          setInfoOpen(true);
          localStorage.setItem(storageKey, 'true');
        }
      } catch (e) {}
    }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Use useSignalEffect to automatically respond to signal changes
  useSignalEffect(() => {
    // Check if user has logged out
    if (store.technician.data.value === null) {
      toast.success(t('messages.loggedOut'));
      navigate('/login');
    }
  });

  const handleLogout = () => {
    handleClose();
    store.technician.logout().catch((error) => {
      console.error('Logout error:', error);
      toast.error(t('messages.logoutFailed'));
    });
  };

  const handleAssistantClick = () => {
    setAssistantOpen(true);
  };

  const handleAssistantClose = () => {
    setAssistantOpen(false);
  };

  // Get technician name from store, safely
  const technicianName = store.technician.data?.value?.profile?.name || '';

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{
              mr: 2,
              color: mode === 'light' ? 'text.secondary' : 'inherit'
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
            {isMobile ? t('app.titleMobile') : t('app.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<WrenchIcon size={20} />}
              onClick={handleAssistantClick}
              sx={{
                mr: 1
              }}
            >
              {!isMobile && t('buttons.installationAssistant')}
            </Button>
            <IconButton
              aria-label={t('buttons.info')}
              title={t('buttons.info')}
              onClick={() => setInfoOpen(true)}
              color="inherit"
              sx={{
                mr: isMobile ? 0 : 2,
                color: mode === 'light' ? 'text.secondary' : 'inherit'
              }}
            >
              <HelpIcon />
            </IconButton>
            <IconButton
              onClick={toggleTheme}
              color="inherit"
              sx={{
                mr: 2,
                color: mode === 'light' ? 'text.secondary' : 'inherit'
              }}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <IconButton
              onClick={handleMenu}
              color="inherit"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              size="small"
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText'
                }}
              >
                {getInitials(technicianName) || ''}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleLogout}>{t('common.logout')}</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <InstallationAssistant
        open={assistantOpen}
        onClose={handleAssistantClose}
      />

      <ServicePointInfoDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
    </>
  );
};

export default Header;
